import { ArrowRight, CheckCircle2, CircleAlert, Eye, MessageSquareQuote, Printer } from "lucide-react";
import { Link } from "react-router-dom";
import { TrustBadge } from "./TrustBadge";

const TONES = {
  act: { wrap: "bg-rose-50 border-rose-200 text-rose-900", Icon: CircleAlert, label: "Action" },
  watch: { wrap: "bg-amber-50 border-amber-200 text-amber-900", Icon: Eye, label: "Watch" },
  ok: { wrap: "bg-emerald-50 border-emerald-200 text-emerald-900", Icon: CheckCircle2, label: "No action" },
  say: { wrap: "bg-stone-100 border-stone-200 text-stone-900", Icon: MessageSquareQuote, label: "In the room" },
  none: { wrap: "bg-stone-50 border-stone-200 text-stone-700", Icon: ArrowRight, label: "Next step" },
};

const FIG_TONES = {
  brand: "border-l-[#A01236] text-[#A01236]",
  emerald: "border-l-emerald-500 text-emerald-700",
  amber: "border-l-amber-500 text-amber-700",
  rose: "border-l-rose-500 text-rose-700",
  sky: "border-l-sky-500 text-sky-700",
  neutral: "border-l-stone-300 text-stone-600",
};

export const DecisionCard = ({ decision, answerability, narrative, printState, printBoard, testId = "decision" }) => {
  if (!decision) return null;
  const { headline, figures = [], action } = decision;
  const tone = TONES[action?.tone] || TONES.none;
  const { Icon } = tone;
  const showAction = action?.text || action?.detail;

  return (
    <div className="space-y-4" data-testid={testId}>
      <div className="space-y-1.5">
        <p className="font-display text-[17px] leading-snug font-semibold text-stone-900"
           data-testid={`${testId}-headline`}>
          {headline}
        </p>
        <TrustBadge answerability={answerability} testId={`${testId}-trust`} inlineCaveats />
      </div>

      {figures.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid={`${testId}-figures`}>
          {figures.map((f, i) => (
            <div key={i}
                 className={`rounded-lg border border-stone-200 border-l-[3px] bg-white px-3.5 py-2.5 ${FIG_TONES[f.tone] || FIG_TONES.neutral}`}
                 data-testid={`${testId}-figure-${i}`}>
              <div className="text-[12px] font-semibold">{f.label}</div>
              <div className="num text-[16px] font-semibold text-stone-900 mt-0.5 leading-tight">{f.value}</div>
              {f.sub && <div className="text-[12px] text-stone-600 mt-0.5 leading-snug">{f.sub}</div>}
            </div>
          ))}
        </div>
      )}

      {showAction && (
        <div className={`border rounded-lg px-3.5 py-3 flex gap-3 ${tone.wrap}`} data-testid={`${testId}-action`}>
          <Icon className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2.2} />
          <div className="min-w-0">
            <div className="text-[12px] font-semibold opacity-80">{tone.label}</div>
            {action.text && (
              <div className="text-[14px] font-medium leading-snug mt-0.5" data-testid={`${testId}-action-text`}>
                {action.text}
              </div>
            )}
            {action.detail && (
              <div className="text-[12px] leading-relaxed mt-1 opacity-90">{action.detail}</div>
            )}
          </div>
        </div>
      )}

      {narrative !== undefined && (
        <p className="text-[14px] text-stone-700 leading-relaxed border-l-2 border-stone-200 pl-3"
           data-testid={`${testId}-narrative`}>
          {narrative || <span className="text-stone-500">one line of reading…</span>}
        </p>
      )}

      {printBoard && (
        <Link to="/board-note" data-testid={`${testId}-print-board`}
              className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#A01236] hover:text-[#760D28]">
          <Printer className="w-3.5 h-3.5" /> Open the printable board note
        </Link>
      )}
      {printState && (
        <Link to={`/brief?state=${encodeURIComponent(printState)}`}
              data-testid={`${testId}-print-brief`}
              className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#A01236] hover:text-[#760D28]">
          <Printer className="w-3.5 h-3.5" /> Print the {printState} one-pager
        </Link>
      )}
    </div>
  );
};
